import { useState, useMemo } from 'react';
import './ToolStyles.css';

interface ColorValues {
    hex: string;
    rgb: string;
    hsl: string;
}

const hexToRgb = (hex: string): [number, number, number] | null => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
    if (!m) return null;
    return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
};

const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
    const rr = r / 255, gg = g / 255, bb = b / 255;
    const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, Math.round(l * 100)];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    switch (max) {
        case rr: h = ((gg - bb) / d + (gg < bb ? 6 : 0)) / 6; break;
        case gg: h = ((bb - rr) / d + 2) / 6; break;
        case bb: h = ((rr - gg) / d + 4) / 6; break;
    }
    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
};

const parseHsl = (hsl: string): [number, number, number] | null => {
    const m = /(\d+)\D+(\d+)%\D+(\d+)%/.exec(hsl.trim());
    if (!m) return null;
    return [Number(m[1]), Number(m[2]), Number(m[3])];
};

const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    const ss = s / 100, ll = l / 100;
    const c = (1 - Math.abs(2 * ll - 1)) * ss;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = ll - c / 2;
    let rr = 0, gg = 0, bb = 0;
    if (h < 60) { rr = c; gg = x; }
    else if (h < 120) { rr = x; gg = c; }
    else if (h < 180) { gg = c; bb = x; }
    else if (h < 240) { gg = x; bb = c; }
    else if (h < 300) { rr = x; bb = c; }
    else { rr = c; bb = x; }
    return [Math.round((rr + m) * 255), Math.round((gg + m) * 255), Math.round((bb + m) * 255)];
};

const rgbToHex = (r: number, g: number, b: number): string =>
    `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;

const ColorConverter = () => {
    const [input, setInput] = useState('');
    const [error, setError] = useState('');

    const result: ColorValues | null = useMemo(() => {
        setError('');
        if (!input.trim()) return null;
        const val = input.trim();

        // 尝试解析 HEX
        const rgb = hexToRgb(val);
        if (rgb) {
            const [r, g, b] = rgb;
            const [h, s, l] = rgbToHsl(r, g, b);
            return { hex: rgbToHex(r, g, b), rgb: `rgb(${r}, ${g}, ${b})`, hsl: `hsl(${h}, ${s}%, ${l}%)` };
        }

        // 尝试解析 HSL
        const hsl = parseHsl(val);
        if (hsl) {
            const [h, s, l] = hsl;
            const [r, g, b] = hslToRgb(h, s, l);
            return { hex: rgbToHex(r, g, b), rgb: `rgb(${r}, ${g}, ${b})`, hsl: `hsl(${h}, ${s}%, ${l}%)` };
        }

        // 尝试解析 RGB
        const rgbMatch = /(\d+)\D+(\d+)\D+(\d+)/.exec(val);
        if (rgbMatch) {
            const r = Number(rgbMatch[1]), g = Number(rgbMatch[2]), b = Number(rgbMatch[3]);
            if (r <= 255 && g <= 255 && b <= 255) {
                const [h, s, l] = rgbToHsl(r, g, b);
                return { hex: rgbToHex(r, g, b), rgb: `rgb(${r}, ${g}, ${b})`, hsl: `hsl(${h}, ${s}%, ${l}%)` };
            }
        }

        setError('无法识别的颜色格式，请输入 HEX（#ff0000）、RGB（255,0,0）或 HSL（0,100%,50%）');
        return null;
    }, [input]);

    const colorHex = result?.hex || '#ccc';

    return (
        <div className="tool-container">
            <div className="input-group">
                <label className="input-label">输入颜色值</label>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                        className="tool-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="例如: #ff0000 / rgb(255,0,0) / hsl(0,100%,50%)"
                        style={{ flex: 1 }}
                    />
                    <div style={{
                        width: 44, height: 44, borderRadius: 12,
                        backgroundColor: result ? colorHex : 'var(--bg-surface-2)',
                        border: '2px solid var(--border-color)',
                        flexShrink: 0,
                        transition: 'background-color 0.3s ease',
                    }} />
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {result && (
                <div className="output-group">
                    <label className="output-label">转换结果</label>
                    {[
                        { label: 'HEX', value: result.hex },
                        { label: 'RGB', value: result.rgb },
                        { label: 'HSL', value: result.hsl },
                    ].map((item) => (
                        <div key={item.label} style={{
                            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
                        }}>
                            <span style={{
                                fontSize: 12, fontWeight: 600, color: 'var(--text-tertiary)',
                                width: 36, flexShrink: 0,
                            }}>{item.label}</span>
                            <code style={{
                                flex: 1, padding: '8px 12px', borderRadius: 8,
                                backgroundColor: 'var(--bg-surface-2)', border: '1px solid var(--border-color)',
                                fontSize: 14, fontFamily: 'monospace',
                            }}>{item.value}</code>
                            <button
                                className="copy-button"
                                onClick={() => navigator.clipboard.writeText(item.value)}
                            >
                                复制
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ColorConverter;
